<template>
  <section class="container mx-auto md:pt-5">
    <!-- Strategy -->
    <div class="strategy py-6 md:py-10">
      <h2
        class="text-center md:text-left section-heading md:w-full mx-auto md:mx-0"
      >
        Strategy
      </h2>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7 md:gap-y-10 mt-8"
      >
        <template v-if="isLoading">
          <div v-for="i in 2" :key="i" class="flex">
            <UtilsSkeletonLoading class="w-7h-7" />
            <div class="ml-4 flex-1">
              <UtilsSkeletonLoading class="w-full mb-2 h-7" />
              <UtilsSkeletonLoading class="w-full h-24" />
            </div>
          </div>
        </template>
        <template v-else>
          <NuxtLink
            v-for="(item, index) in strategy"
            :key="index"
            :to="`/expertises/${item?.attributes?.slug}`"
            :title="item?.attributes?.title"
            class="flex flex-col gap-3 expertise-card p-4 border-b-2 border-transparent hover:border-b-2 hover:border-primary-primary"
          >
            <div class="expertise-title flex gap-4 items-center">
              <NuxtImg
                v-if="item.attributes?.icon?.data"
                format="webp"
                srcset=""
                loading="lazy"
                class="w-8 h-8 icon"
                :src="item.attributes?.icon?.data?.attributes?.url"
                :alt="item.attributes?.title"
                :title="item.attributes?.title"
              />
              <h3 class="text-base md:text-lg text-bw-900 title">
                {{ item?.attributes?.title }}
              </h3>
              <NuxtIcon name="chevron-right" />
            </div>
            <div class="expertise-desc">
              <p class="text-sm md:text-base text-bw-900">
                {{ item?.attributes?.description }}
              </p>
            </div>
          </NuxtLink>
        </template>
      </div>
    </div>

    <!-- Creative -->
    <div class="creative py-6 md:py-10">
      <h2
        class="text-center md:text-left section-heading md:w-full mx-auto md:mx-0"
      >
        Creative
      </h2>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7 md:gap-y-10 mt-8"
      >
        <template v-if="isLoading">
          <div v-for="i in 2" :key="i" class="flex">
            <UtilsSkeletonLoading class="w-7h-7" />
            <div class="ml-4 flex-1">
              <UtilsSkeletonLoading class="w-full mb-2 h-7" />
              <UtilsSkeletonLoading class="w-full h-24" />
            </div>
          </div>
        </template>
        <template v-else>
          <NuxtLink
            v-for="(item, index) in creative"
            :key="index"
            :to="`/expertises/${item?.attributes?.slug}`"
            :title="item?.attributes?.title"
            class="flex flex-col gap-3 expertise-card p-4 border-b-2 border-transparent hover:border-b-2 hover:border-primary-primary"
          >
            <div class="expertise-title flex gap-4 items-center">
              <NuxtImg
                format="webp"
                srcset=""
                loading="lazy"
                class="w-8 h-8 icon"
                :src="item.attributes?.icon?.data?.attributes?.url"
                :alt="item.attributes?.title"
                :title="item.attributes?.title"
              />
              <h3 class="text-base md:text-lg text-bw-900 title">
                {{ item?.attributes?.title }}
              </h3>
              <NuxtIcon name="chevron-right" />
            </div>
            <div class="expertise-desc">
              <p class="text-sm md:text-base text-bw-900">
                {{ item?.attributes?.description }}
              </p>
            </div>
          </NuxtLink>
        </template>
      </div>
    </div>

    <!-- Technology -->
    <div class="technology py-6 md:py-10">
      <h2
        class="text-center md:text-left section-heading md:w-full mx-auto md:mx-0"
      >
        Technology
      </h2>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7 md:gap-y-10 mt-8"
      >
        <template v-if="isLoading">
          <div v-for="i in 2" :key="i" class="flex">
            <UtilsSkeletonLoading class="w-7h-7" />
            <div class="ml-4 flex-1">
              <UtilsSkeletonLoading class="w-full mb-2 h-7" />
              <UtilsSkeletonLoading class="w-full h-24" />
            </div>
          </div>
        </template>
        <template v-else>
          <NuxtLink
            v-for="(item, index) in technology"
            :key="index"
            :to="`/expertises/${item?.attributes?.slug}`"
            :title="item?.attributes?.title"
            class="flex flex-col gap-3 expertise-card p-4 border-b-2 border-transparent hover:border-b-2 hover:border-primary-primary"
          >
            <div class="expertise-title flex gap-4 items-center">
              <NuxtImg
                format="webp"
                srcset=""
                loading="lazy"
                class="w-8 h-8 icon"
                :src="item.attributes?.icon?.data?.attributes?.url"
                :alt="item.attributes?.title"
                :title="item.attributes?.title"
              />
              <h3 class="text-base md:text-lg text-bw-900 title">
                {{ item?.attributes?.title }}
              </h3>
              <NuxtIcon name="chevron-right" />
            </div>
            <div class="expertise-desc">
              <p class="text-sm md:text-base text-bw-900">
                {{ item?.attributes?.description }}
              </p>
            </div>
          </NuxtLink>
        </template>
      </div>
    </div>

    <!-- Communication -->
    <div class="communication py-6 md:py-10">
      <h2
        class="text-center md:text-left section-heading md:w-full mx-auto md:mx-0"
      >
        Communication
      </h2>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7 md:gap-y-10 mt-8"
      >
        <template v-if="isLoading">
          <div v-for="i in 2" :key="i" class="flex">
            <UtilsSkeletonLoading class="w-7h-7" />
            <div class="ml-4 flex-1">
              <UtilsSkeletonLoading class="w-full mb-2 h-7" />
              <UtilsSkeletonLoading class="w-full h-24" />
            </div>
          </div>
        </template>
        <template v-else>
          <NuxtLink
            v-for="(item, index) in communication"
            :key="index"
            :to="`/expertises/${item?.attributes?.slug}`"
            :title="item?.attributes?.title"
            class="flex flex-col gap-3 expertise-card p-4 border-b-2 border-transparent hover:border-b-2 hover:border-primary-primary"
          >
            <div class="expertise-title flex gap-4 items-center">
              <NuxtImg
                format="webp"
                srcset=""
                loading="lazy"
                class="w-8 h-8 icon"
                :src="item.attributes?.icon?.data?.attributes?.url"
                :alt="item.attributes?.title"
                :title="item.attributes?.title"
              />
              <h3 class="text-base md:text-lg text-bw-900 title">
                {{ item?.attributes?.title }}
              </h3>
              <NuxtIcon name="chevron-right" />
            </div>
            <div class="expertise-desc">
              <p class="text-sm md:text-base text-bw-900">
                {{ item?.attributes?.description }}
              </p>
            </div>
          </NuxtLink>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
const isLoading = ref(true)

const expertisesData = ref([])
const strategy = ref([])
const creative = ref([])
const technology = ref([])
const communication = ref([])

const getExpertisesList = async () => {
  const { data, error, pending } = await useSuitmediaApiData(
    `/api/expertices`,
    {
      query: {
        'populate[icon][fields][0]': 'url',
        'fields[0]': 'id',
        'fields[1]': 'title',
        'fields[2]': 'description',
        'fields[3]': 'slug',
        'fields[4]': 'expertice_category',
        'fields[5]': 'order',
        'sort[5]': 'order',
        'populate[expertice_category][fields][0]': 'title',
        'populate[expertice_category][fields][1]': 'slug'
      }
    }
  )

  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Expertises does not exist`
    })
  }

  return data.value
}

const expertises = await getExpertisesList()
expertisesData.value = expertises.data
strategy.value = expertises.data.filter(
  item =>
    item.attributes?.expertice_category?.data?.attributes?.title === 'Strategy'
)
creative.value = expertises.data.filter(
  item =>
    item.attributes?.expertice_category?.data?.attributes?.title === 'Creative'
)
technology.value = expertises.data.filter(
  item =>
    item.attributes?.expertice_category?.data?.attributes?.title ===
    'Technology'
)
communication.value = expertises.data.filter(
  item =>
    item.attributes?.expertice_category?.data?.attributes?.title ===
    'Communication'
)
</script>

<style lang="scss" scoped>
.expertise-card {
  .expertise-title {
    font-weight: 500;
  }

  &:hover {
    box-shadow: 0px 2px 10px 0px #0000001a;

    .expertise-title {
      .title {
        color: #ff6700;
      }
      .icon {
        filter: invert(49%) sepia(56%) saturate(4532%) hue-rotate(0deg)
          brightness(103%) contrast(106%);
      }
    }

    :deep(.nuxt-icon) {
      svg {
        path {
          fill: #ff6700 !important;
        }
      }
    }
  }
}

:deep(.automotive-icon) {
  svg {
    width: 32px;
    height: 32px;
  }
}
</style>
